import invariant from './invariant';
import { has } from 'lodash';
import { simpleNumberLocalizer } from '../localizers/simple-number-localizer';
import { simpleDateLocalizer } from '../localizers/simple-date-localizer';
import { simpleMessageLocalizer } from '../localizers/simple-message-localizer';

function emptyLocalizer(props: string[]) {
  const empty = {};

  if (window?.process?.env?.NODE_ENV !== 'production') {
    props
      .forEach((name) => Object.defineProperty(empty, name, {
        enumerable: true,
        get() {
          throw new Error(
            '[adp-react-components] You are attempting to use a widget that requires localization.' +
            'However there is no localizer set. Please configure a localizer.' +
            'Missing localizer method: ' + name);
        }
      }));
  }
  return empty;
}

export interface INumberLocalizer {
  formats: {
    default: any;
    editFormat?: (str: string) => number;
  };
  parse: (num: string, locale: string | undefined, format?: any) => number | null | undefined;
  format: (num: number, format: any, locale?: string) => string;
  precision: (format: any, locale?: string) => number | null | undefined;
  decimalChar: (format: any, locale?: string) => string;
}

export interface IDateLocalizer {
  formats: {
    default: any;
    date: any;
    time: any;
    header: any;
    footer: any;
    weekday: any;
    dayOfMonth: any;
    month: any;
    year: any;
    decade: any;
    century: any;
  };
  startOfWeek: (locale?: string) => number;
  parse: (date: string, format: any, locale?: string) => Date | null | undefined;
  format: (date: Date | undefined, format: any, locale?: string) => string;
}

export interface IMessageLocalizer {
  formatMessage: (path: string | string[], variables?: string | string[] | object, locale?: string) => string;
}

const REQUIRED_NUMBER_FORMATS = [ 'default' ];

const REQUIRED_DATE_FORMATS = [
  'default',
  'date',
  'time',
  'header',
  'footer',
  'dayOfMonth',
  'month',
  'year',
  'decade',
  'century'
];

function _format(localizer, formatter, value, format, locale) {
  const result = typeof format === 'function' ? format(value, locale, localizer) : formatter.call(localizer, value, format, locale);

  invariant(result == null || typeof result === 'string', '`localizer format(..)` must return a string, null, or undefined');

  return result;
}

function checkFormats(requiredFormats, formats) {
  if (window?.process?.env?.NODE_ENV !== 'production') {
    requiredFormats.forEach((f) => invariant(has(formats, f), 'localizer missing required format: `%s`', f));
  }
}

let _numberLocalizer = emptyLocalizer(['formats', 'parse', 'format', 'precision', 'decimalChar']) as INumberLocalizer;

export function setNumber(localizer: INumberLocalizer) {
  const { format, parse, formats } = localizer;
  let { precision, decimalChar } = localizer;

  invariant(typeof format === 'function', 'number localizer `format(..)` must be a function');
  invariant(typeof parse === 'function', 'number localizer `parse(..)` must be a function');

  checkFormats(REQUIRED_NUMBER_FORMATS, formats);

  formats.editFormat = formats.editFormat || ((str) => parseFloat(str));

  precision = precision || (() => null);

  decimalChar = decimalChar || (() => '.');

  _numberLocalizer = {
    formats,
    precision,
    decimalChar,

    format(value: number, str: string, locale?: string) {
      return _format(this, format, value, str, locale);
    },

    parse(value: string, locale: string | undefined, formatStr?: string) {
      const result = parse.call(this, value, locale, formatStr);
      invariant(result == null || typeof result === 'number', 'number localizer `parse(..)` must return a number, null, or undefined');
      return result;
    }
  };
}

let _dateLocalizer = emptyLocalizer(['formats', 'parse', 'format', 'firstOfWeek', 'startOfWeek']) as IDateLocalizer;

export function setDate(localizer: IDateLocalizer) {
  const { format, parse, formats, startOfWeek } = localizer;
  invariant(typeof format === 'function', 'date localizer `format(..)` must be a function');
  invariant(typeof parse === 'function', 'date localizer `parse(..)` must be a function');
  invariant(typeof startOfWeek === 'function', 'date localizer `startOfWeek(..)` must be a function');
  checkFormats(REQUIRED_DATE_FORMATS, formats);

  _dateLocalizer = {
    formats,
    startOfWeek,

    format(value: Date, str: string, locale?: string) {
      return _format(this, format, value, str, locale);
    },

    parse(value: string, formatStr: string, locale?: string) {
      const result = parse.call(this, value, formatStr, locale);
      invariant(result == null || (result instanceof Date && !isNaN(result.getTime())), 'date localizer `parse(..)` must return a valid Date, null, or undefined');
      return result;
    }
  };
}

let _messageLocalizer = emptyLocalizer(['formatMessage']) as IMessageLocalizer;

export function setMessage(localizer: IMessageLocalizer) {
  const { formatMessage } = localizer;
  invariant(typeof formatMessage === 'function', 'message localizer `formatMessage(..)` must be a function');

  _messageLocalizer = {
    formatMessage
  };
}

setNumber(simpleNumberLocalizer());
setDate(simpleDateLocalizer());
setMessage(simpleMessageLocalizer());

export const number = {
  getFormat(key: string, format: any) {
    return format || _numberLocalizer.formats[key];
  },
  parse(num: string, locale: string | undefined, format?: any) {
    return _numberLocalizer.parse(num, locale, format);
  },
  format(num: number, format: any, locale?: string) {
    return _numberLocalizer.format(num, format, locale);
  },
  decimalChar(format: any, locale?: string) {
    return _numberLocalizer.decimalChar(format, locale);
  },
  precision(format: any, locale?: string) {
    return _numberLocalizer.precision(format, locale);
  }
};

export const date = {
  getFormat(key: string, format?: any) {
    return format || _dateLocalizer.formats[key];
  },
  parse(dateStr: string, format: any, locale?: string) {
    return _dateLocalizer.parse(dateStr, format, locale);
  },
  format(dateVal: Date | undefined, format: any, locale?: string) {
    return _dateLocalizer.format(dateVal, format, locale);
  },
  startOfWeek(locale?: string) {
    return _dateLocalizer.startOfWeek(locale);
  }
};

export const message = {
  formatMessage(path: string | string[], variables?: string | string[] | object, locale?: string) {
    return _messageLocalizer.formatMessage(path, variables, locale);
  }
};

export default { number, date, message };
